import React, { useState } from "react";
import { useAuthState } from "@features/auth/store/authStore";
import VStack from "@components/layout/VStack";
import HStack from "@components/layout/HStack";
import Button from "@components/ui/Button";
import { msalAdapter } from "@features/auth/services/msalAdapter";

const AuthTest: React.FC = () => {
    const { tokens, user, isLoading: isAuthLoading } = useAuthState();
    const [apiResult, setApiResult] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [apiToken, setApiToken] = useState<string>("");
    const [tokenDetails, setTokenDetails] = useState<any>(null);

    // Sử dụng Full Scope URI từ screenshot Azure Portal của bạn
    const API_SCOPE = "api://rc8.add-on-company.com/43d2b2f6-b906-421c-bc40-a5b1b5aa571f/access_as_user";

    const handleReAuth = async () => {
        try {
            await msalAdapter.initialize();
            setApiToken("");
            setTokenDetails(null);
        } catch (err: any) {
            console.error("Re-auth Error:", err);
            setError("Authentication failed: " + err.message);
        }
    };

    const handleClear = () => {
        setApiResult(null);
        setError(null);
        setApiToken("");
        setTokenDetails(null);
    };

    const decodeToken = (token: string) => {
        try {
            const base64Url = token.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            }).join(''));
            return JSON.parse(jsonPayload);
        } catch (e) {
            return null;
        }
    };

    const callAuthApi = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const token = await msalAdapter.getValidToken([API_SCOPE]);
            setApiToken(token);
            setTokenDetails(decodeToken(token));

            const response = await fetch("https://localhost:7278/api/Me", {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`API error: ${response.status} ${response.statusText} - ${errorText}`);
            }

            const data = await response.json();
            setApiResult(data);
        } catch (err: any) {
            console.error("Test API Error:", err);
            setError(err.message || "Failed to call API");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <VStack className="p-4 space-y-4" gap={4}>
            <h1 className="text-xl font-bold">Auth Test View</h1>

            <div className="bg-gray-100 p-3 rounded dark:bg-gray-800 border-l-4 border-blue-500">
                <h2 className="font-semibold text-blue-600">Token Info (To be sent):</h2>
                {tokenDetails ? (
                    <div className="text-xs space-y-1 mt-1">
                        <p><strong>Aud:</strong> {tokenDetails.aud}</p>
                        <p><strong>Iss:</strong> {tokenDetails.iss}</p>
                        <p><strong>Ver:</strong> {tokenDetails.ver}</p>
                        <p><strong>Scp:</strong> {tokenDetails.scp}</p>
                    </div>
                ) : (
                    <p className="text-xs italic text-gray-500">Click Call API to inspect token</p>
                )}
            </div>

            <div className="bg-gray-100 p-3 rounded dark:bg-gray-800 border-l-4 border-green-500">
                <h2 className="font-semibold text-green-600">Full API Token:</h2>
                <div className="text-xs break-all border p-1 bg-white dark:bg-black max-h-20 overflow-auto">
                    {apiToken || "No API token requested yet"}
                </div>
            </div>

            <HStack gap={2} className="w-full">
                <Button
                    label={isLoading ? "Calling API..." : "Call API"}
                    onClick={callAuthApi}
                    disabled={isLoading}
                    className="flex-1"
                    isDefault={true}
                />
                <Button
                    label={isAuthLoading ? "Authenticating..." : "Re-authenticate"}
                    onClick={handleReAuth}
                    disabled={isAuthLoading}
                    className="flex-1 bg-gray-200 dark:bg-gray-700"
                />
            </HStack>

            <Button
                label="Clear Results"
                onClick={handleClear}
                className="w-full border border-gray-300 dark:border-gray-600"
            />

            {error && (
                <div className="text-red-500 text-sm p-2 border border-red-500 rounded">
                    <strong>Error:</strong> {error}
                </div>
            )}

            {apiResult && (
                <div className="bg-green-100 p-3 rounded dark:bg-green-900 border-l-4 border-green-500">
                    <h2 className="font-semibold text-green-700 dark:text-green-300">API Response:</h2>
                    <pre className="text-xs mt-1">
                        {JSON.stringify(apiResult, null, 2)}
                    </pre>
                </div>
            )}
        </VStack>
    );
};

export default AuthTest;
